/**
 * A4 print CSS for the Monthly Consultation PDF path (Puppeteer / print preview).
 *
 * Inject via `<style>{MONTHLY_CONSULTATION_PDF_STYLES}</style>` on the print document
 * root (T8). Styles are scoped under `[data-mc-pdf-root]` so they do not bleed into
 * the interactive web Monthly Consultation viewer.
 *
 * Cream / charcoal values come from Canva redesign tokens in `pdfTheme.ts`.
 *
 * ## Class names (atoms / chapters apply these)
 *
 * | Class | Role |
 * |-------|------|
 * | `.mc-pdf-page` | Page canvas: cream + dot-grid, inset padding, min-height 297mm |
 * | `.mc-pdf-page--cover` | Full-bleed cover: no inset/dot-grid; min-height 297mm |
 * | `.mc-pdf-cover-poster` | Cover flex stack (bars + title + brand); fills A4 |
 * | `.mc-pdf-cover-bg` | Absolute full-bleed cover art (`object-fit: cover`) |
 * | `.mc-pdf-chapter` | Chapter shell; forces a page break before every chapter except the first |
 * | `.mc-pdf-header` | Top chrome row (eyebrow left, page label right) |
 * | `.mc-pdf-eyebrow` | Small-caps section label (top-left, terracotta) |
 * | `.mc-pdf-page-label` | Chapter / page label (top-right) |
 * | `.mc-pdf-card` | White content card — `break-inside: avoid` |
 * | `.mc-pdf-dark-callout` | Charcoal insight / quote / trap bar — `break-inside: avoid` |
 * | `.mc-pdf-footer` | Bottom chrome (product label + PAGE NN); kept visible in print |
 * | `.mc-pdf-corner-luopan` | Top-right gold luopan PNG; bleeds off the page |
 * | `.mc-pdf-corner-luopan-band` | Relative header band with padding-right for the luopan |
 * | `.mc-pdf-nobleman-portrait` | Standalone nobleman illustration band |
 * | `.mc-pdf-nobleman-hero` | 4:5 full-bleed portrait slot at top of nobleman card |
 * | `.print-hide` | Web / interactive chrome hidden when printing |
 *
 * ## Puppeteer / Chromium fragmentation notes
 *
 * Do NOT set `overflow: hidden` (or non-visible overflow) on `html`, `body`, or
 * `[data-mc-pdf-root]` — that creates a fragmentation root and Chromium drops all
 * pages after the first in `page.pdf()`. Prefer `overflow: visible`.
 *
 * Owned by T2 — isolated from `alignment-advantage/print/printStyles.ts`.
 */

import {
  bodyFontFamily,
  border,
  cardWhite,
  charcoal,
  cream,
  displayFontFamily,
  mutedGray,
  paleTan,
  terracotta,
} from "./pdfTheme";

/**
 * Full CSS string for Monthly Consultation PDF print. Safe to inject as a style tag.
 */
export const MONTHLY_CONSULTATION_PDF_STYLES = `
  @page {
    size: A4;
    margin: 0;
  }

  /*
   * Exact color reproduction for cream, dots, cards, and callouts.
   * Scoped to the MC PDF root (+ descendants) to avoid affecting other routes
   * if this stylesheet is ever present outside the print document.
   */
  [data-mc-pdf-root],
  [data-mc-pdf-root] * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  @media screen {
    [data-mc-pdf-root] {
      max-width: 860px;
      margin: 0 auto;
      padding: 24px 32px;
      background: ${cream};
      color: ${charcoal};
      box-sizing: border-box;
    }
  }

  @media print {
    @page {
      size: A4;
      margin: 0;
    }

    html,
    body {
      background: ${cream} !important;
      margin: 0 !important;
      padding: 0 !important;
      width: 100% !important;
      max-width: 100% !important;
      /*
       * Do NOT set overflow:hidden (or overflow-x:hidden) here — any non-visible
       * overflow creates a fragmentation root and Chromium drops all pages after
       * the first in page.pdf().
       */
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    [data-mc-pdf-root] {
      background: ${cream} !important;
      color: ${charcoal} !important;
      max-width: none !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      box-sizing: border-box !important;
      overflow: visible !important;
    }

    /* Hide web / interactive chrome on the print route */
    .print-hide {
      display: none !important;
    }

    /*
     * Page canvas — Canva cream + subtle charcoal dot grid.
     * Used by PdfChapterShell (or equivalent) as the per-chapter surface.
     */
    [data-mc-pdf-root] .mc-pdf-page {
      background-color: ${cream} !important;
      background-image: radial-gradient(
        circle,
        rgba(26, 30, 63, 0.1) 1px,
        transparent 1px
      ) !important;
      background-size: 14px 14px !important;
      background-position: 0 0 !important;
      color: ${charcoal} !important;
      box-sizing: border-box !important;
      padding: 14mm 14mm !important;
      width: 100% !important;
      max-width: 100% !important;
      /*
       * Every printed sheet is at least one A4 tall so short chapters do not
       * shrink-wrap. Prefer min-height (not fixed height) so dense chapters can
       * still grow onto a second sheet without a blank forced page.
       */
      min-height: 297mm !important;
      display: flex !important;
      flex-direction: column !important;
      overflow: visible !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }

    /*
     * Each chapter starts on a fresh page, except the first — otherwise Chromium
     * emits a blank leading page before cover.
     * Chapters may span multiple A4 pages; do NOT use break-inside: avoid here.
     */
    /*
     * Default chapters start on a fresh page (except the first).
     * .mc-pdf-chapter--auto remains available for rare dual-pack cases; Phase 4
     * chapters (Trap / Weeks / Rarity / Stars) each own a full A4 sheet.
     */
    [data-mc-pdf-root] .mc-pdf-chapter:not(:first-child):not(.mc-pdf-chapter--auto) {
      break-before: page;
      page-break-before: always;
    }

    [data-mc-pdf-root] .mc-pdf-chapter--auto {
      break-before: auto;
      page-break-before: auto;
    }

    [data-mc-pdf-root] .mc-pdf-chapter {
      break-inside: auto;
      page-break-inside: auto;
      overflow: visible !important;
    }

    /* Cards and dark callouts stay intact across page boundaries when possible */
    [data-mc-pdf-root] .mc-pdf-card {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    [data-mc-pdf-root] .mc-pdf-dark-callout {
      break-inside: avoid;
      page-break-inside: avoid;
    }

    /*
     * Footer chrome must remain visible in print (do not share .print-hide).
     * Avoid orphaning the footer alone on the next A4 sheet.
     * margin-top:auto pins the footer to the bottom of a short A4 sheet when
     * the page is a flex column (see .mc-pdf-page + PdfChapterShell).
     */
    [data-mc-pdf-root] .mc-pdf-footer {
      display: flex !important;
      visibility: visible !important;
      margin-top: auto !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      break-inside: avoid;
      page-break-inside: avoid;
      break-before: avoid;
      page-break-before: avoid;
    }

    [data-mc-pdf-root] .mc-pdf-header {
      display: flex !important;
      visibility: visible !important;
      justify-content: space-between;
      align-items: baseline;
      width: 100%;
      box-sizing: border-box;
    }

    [data-mc-pdf-root] img,
    [data-mc-pdf-root] svg,
    [data-mc-pdf-root] table {
      max-width: 100% !important;
    }

    /*
     * Cover poster is full-bleed (top gradient + bottom brand bar).
     * Drop the 14mm inset and the inner-page dot grid.
     * Force at least one A4 tall so absolute .mc-pdf-cover-bg paints
     * edge-to-edge (min-height:100% alone fails in print — parents have
     * no resolved height). Prefer min-height over fixed height to avoid
     * a blank second sheet from subpixel overflow.
     */
    [data-mc-pdf-root] .mc-pdf-page.mc-pdf-page--cover {
      padding: 0 !important;
      background-image: none !important;
      background-color: ${cream} !important;
      min-height: 297mm !important;
      box-sizing: border-box !important;
    }

    [data-mc-pdf-root] .mc-pdf-page.mc-pdf-page--cover > div {
      flex: 1 1 auto !important;
      min-height: 297mm !important;
      display: flex !important;
      flex-direction: column !important;
    }

    [data-mc-pdf-root] .mc-pdf-cover-poster {
      flex: 1 1 auto !important;
      min-height: 297mm !important;
    }
  }

  /*
   * Screen preview mirrors print canvas so local preview matches PDF output.
   * Fragmentation rules still apply only under @media print above.
   */
  @media screen {
    [data-mc-pdf-root] .mc-pdf-page {
      background-color: ${cream};
      background-image: radial-gradient(
        circle,
        rgba(26, 30, 63, 0.1) 1px,
        transparent 1px
      );
      background-size: 14px 14px;
      background-position: 0 0;
      color: ${charcoal};
      box-sizing: border-box;
      padding: 14mm 14mm;
      width: 100%;
      max-width: 100%;
      /* Match print: each sheet preview is at least one A4 tall. */
      min-height: 297mm;
      display: flex;
      flex-direction: column;
    }

    [data-mc-pdf-root] .mc-pdf-footer {
      margin-top: auto;
    }

    [data-mc-pdf-root] .mc-pdf-chapter:not(:first-child) {
      margin-top: 32px;
      padding-top: 32px;
      border-top: 1px dashed rgba(26, 30, 63, 0.15);
    }

    [data-mc-pdf-root] .mc-pdf-page.mc-pdf-page--cover {
      padding: 0;
      background-image: none;
      background-color: ${cream};
      min-height: 297mm;
      box-sizing: border-box;
    }

    [data-mc-pdf-root] .mc-pdf-page.mc-pdf-page--cover > div {
      flex: 1 1 auto;
      min-height: 297mm;
      display: flex;
      flex-direction: column;
    }

    [data-mc-pdf-root] .mc-pdf-cover-poster {
      flex: 1 1 auto;
      min-height: 297mm;
    }
  }

  /*
   * Hero photos — A4-width bands (object-fit cover). Not letterboxed posters.
   * Heights match Canva page bands (~20-35% of sheet): Overview 152px,
   * Alignment 190px, Focus 232px, Aspect 190px (career / wealth /
   * relationship), Body 190px. Compact chapter spacing keeps one A4 each.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img {
    width: 100%;
    display: block;
    object-fit: cover;
    object-position: center;
    border-radius: 14px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /*
   * Overview mountain sunrise — Canva page-02 is a substantial ~20-22% band
   * (not a thin strip). object-position keeps the sun and mid ridges in
   * frame when cover-crops into a wide strip.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--overview {
    height: 250px;
    object-position: center 38%;
  }

  /*
   * Alignment handshake — landscape 800×512 wrist-grip. Canva page-05 uses a
   * substantial rounded band (~22-25% of the sheet); taller band also eats
   * leftover cream below the executive insight.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--alignment {
    height: 230px;
    object-position: center 48%;
  }

  /* Personal-note mountain seal (circular gold line-art). */
  [data-mc-pdf-root] .mc-pdf-alignment-note-seal {
    width: 72px;
    height: 72px;
    max-width: none !important;
    border-radius: 50%;
    object-fit: cover;
    object-position: center;
    display: block;
    flex-shrink: 0;
  }

  /*
   * Alignment Do/Don't row — content-sized (do not stretch into empty cream).
   */
  [data-mc-pdf-root] .mc-pdf-alignment-guidance {
    flex: 0 0 auto;
    align-content: start;
  }

  [data-mc-pdf-root] .mc-pdf-scorecard-areas {
    flex: 0 0 auto;
    align-content: start;
    gap: 12px;
  }

  [data-mc-pdf-root] .mc-pdf-scorecard-area-card {
    flex: 0 0 auto;
  }

  /*
   * Focus calendar hero — user PNG 800x512 (desk calendar, pink pin on 18).
   * Canva page-03 uses a tall ~30-35% rounded band under the title; cover
   * keeps the pin readable while compact mantra / window / expect stacks
   * still fit one A4 sheet.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--focus {
    height: 280px;
    object-position: center 42%;
  }

  /*
   * Rarity / Why it is yours — generated celestial hero fills leftover A4
   * space after short proof copy. Wide landscape band under the title.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--rarity {
    height: 200px;
    object-position: center 42%;
  }

  /*
   * Stars in the activated palace — AI celestial / constellation band.
   * Moderate height so definitions + star cues still fit one A4 sheet.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--stars {
    height: 148px;
    object-position: center 40%;
  }

  [data-mc-pdf-root] .mc-pdf-rarity-stack-art {
    width: 120px;
    height: 120px;
    max-width: none !important;
    object-fit: contain;
    display: block;
    flex-shrink: 0;
    border-radius: 14px;
  }

  [data-mc-pdf-root] .mc-pdf-hero-img--aspect {
    height: 350px;
  }

  /*
   * Career staircase hero — user JPEG 6144x4096 (wooden steps + hand).
   * Shared aspect band is 300px tall; bias slightly below center so the
   * ascending blocks and placement hand stay in frame (Canva page-07).
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--career {
    object-position: center 58%;
  }

  /*
   * Wealth cubes hero — user JPEG 5885x3871 (hand flipping H of WEALTH).
   * Blocks sit slightly below vertical center; nudge down so the full
   * letter row and gripping hand stay in the 300px aspect band.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--wealth {
    object-position: center 55%;
  }

  /*
   * Relationship pinky-swear hero — user JPEG 6016x4016 (interlocked
   * little fingers, warm outdoor bokeh). Focal point is dead-center;
   * slight upward bias keeps the joined pinkies dominant in crop.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--relationships {
    object-position: center 45%;
  }

  /*
   * Body / health landscape — Canva page-10 uses a ~25-30% rounded band
   * above the week timeline; compact body sections keep one A4 sheet.
   */
  [data-mc-pdf-root] .mc-pdf-hero-img--body {
    height: 300px;
  }

  /*
   * Header luopan — Photoroom 32bppArgb PNG: gold wheel, purple center,
   * scattered gold stars, transparent field so cream #fdfbf7 shows through.
   * Portrait 681x1024; box matches that ratio so contain does not shrink
   * the wheel. Do not rely on border-radius / overflow clipping (ignored on
   * replaced images in Chromium Print Preview). Global img max-width:100%
   * would squash the page-edge bleed, so this class keeps max-width/height
   * none. Shared by Activations, Weeks, and Letter.
   */
  [data-mc-pdf-root] .mc-pdf-corner-luopan,
  [data-mc-pdf-root] .mc-pdf-activations-wheel {
    position: absolute;
    top: -64mm;
    right: -32mm;
    width: 90mm;
    height: 135mm;
    max-width: none !important;
    max-height: none !important;
    display: block;
    object-fit: contain;
    object-position: center center;
    pointer-events: none;
    z-index: 0;
  }

  [data-mc-pdf-root] .mc-pdf-corner-luopan-band,
  [data-mc-pdf-root] .mc-pdf-activations-hero {
    position: relative;
    z-index: 1;
    padding-right: 48mm;
    box-sizing: border-box;
  }

  [data-mc-pdf-root] .mc-pdf-activations-hero {
    min-height: 56mm;
  }

  /*
   * Nobleman cards — portrait is the card top (full bleed), not a nested frame.
   * Hero band uses 4:5 aspect like the web Allies chapter; body sits below.
   */
  [data-mc-pdf-root] .mc-pdf-nobleman-portrait {
    width: 100%;
    max-width: 100% !important;
    height: 280px;
    display: block;
    object-fit: contain !important;
    object-position: center bottom !important;
    background-color: ${cream};
  }

  [data-mc-pdf-root] .mc-pdf-nobleman-hero {
    width: 100%;
    aspect-ratio: 4 / 5;
    overflow: hidden;
    line-height: 0;
    flex-shrink: 0;
  }

  [data-mc-pdf-root] .mc-pdf-nobleman-portrait--card {
    width: 100%;
    height: 100%;
    max-height: none !important;
    object-fit: cover !important;
    object-position: center top !important;
    background-color: transparent;
    border-radius: 0;
    display: block;
  }

  [data-mc-pdf-root] .mc-pdf-nobleman-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px 14px 14px;
    flex-shrink: 0;
  }

  [data-mc-pdf-root] .mc-pdf-nobleman-grid {
    align-items: start;
  }

  [data-mc-pdf-root] .mc-pdf-nobleman-card {
    overflow: hidden;
    padding: 0 !important;
  }

  /* Year Map — larger radial donut on print (Canva page-14). */
  [data-mc-pdf-root] .mc-pdf-year-radial svg {
    max-width: none !important;
  }

  [data-mc-pdf-root] .mc-pdf-year-month-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    align-content: start;
  }

  [data-mc-pdf-root] .mc-pdf-year-month-card {
    position: relative;
    overflow: hidden;
    border-radius: 12px;
    padding: 10px 48px 10px 12px;
    min-height: 78px;
  }

  [data-mc-pdf-root] .mc-pdf-year-month-icon {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.52;
    line-height: 0;
    pointer-events: none;
  }

  [data-mc-pdf-root] .mc-pdf-year-month-head {
    display: flex;
    align-items: center;
    gap: 7px;
  }

  [data-mc-pdf-root] .mc-pdf-year-month-label {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    color: ${charcoal};
  }

  [data-mc-pdf-root] .mc-pdf-year-month-dot {
    width: 8px;
    height: 8px;
    border-radius: 999px;
    display: inline-block;
    flex-shrink: 0;
  }

  [data-mc-pdf-root] .mc-pdf-year-month-palace {
    margin: 4px 0 0;
    font-family: ${displayFontFamily};
    font-size: 12px;
    font-weight: 700;
    color: ${charcoal};
    line-height: 1.3;
  }

  [data-mc-pdf-root] .mc-pdf-year-month-season {
    margin: 2px 0 0;
    font-size: 10px;
    color: ${mutedGray};
  }

  [data-mc-pdf-root] .mc-pdf-year-month-status {
    margin: 5px 0 0;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  /* Letter — A note for you (Canva page-15). */
  [data-mc-pdf-root] .mc-pdf-page[data-pdf-chapter="letter"] > div {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  [data-mc-pdf-root] .mc-pdf-letter-page {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
    font-family: ${bodyFontFamily};
    gap: 14px;
  }

  [data-mc-pdf-root] .mc-pdf-letter-hero {
    flex-shrink: 0;
  }

  /*
   * Keep contract / next / note tightly stacked (Canva density).
   * Do NOT space-between — leftover A4 height goes to the art band.
   */
  [data-mc-pdf-root] .mc-pdf-letter-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex-shrink: 0;
  }

  [data-mc-pdf-root] .mc-pdf-letter-art-band {
    flex: 1 1 auto;
    min-height: 72px;
    position: relative;
    overflow: visible;
  }

  [data-mc-pdf-root] .mc-pdf-letter-art {
    position: absolute;
    right: 0;
    bottom: 0;
    width: 220px;
    max-width: 48%;
    height: auto;
    max-height: 100%;
    object-fit: contain;
    object-position: right bottom;
    opacity: 0.55;
    pointer-events: none;
  }

  [data-mc-pdf-root] .mc-pdf-letter-executive {
    flex-shrink: 0;
  }

  [data-mc-pdf-root] .mc-pdf-letter-head {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 10px;
    position: relative;
    z-index: 1;
  }

  [data-mc-pdf-root] .mc-pdf-letter-head-icon {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: ${paleTan};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
  }

  [data-mc-pdf-root] .mc-pdf-letter-eyebrow {
    margin: 0;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${terracotta};
  }

  [data-mc-pdf-root] .mc-pdf-letter-title {
    margin: 4px 0 0;
    font-family: ${displayFontFamily};
    font-size: 24px;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: -0.02em;
    color: ${charcoal};
  }

  [data-mc-pdf-root] .mc-pdf-letter-greeting {
    margin: 0 0 6px;
    position: relative;
    z-index: 1;
    font-family: ${displayFontFamily};
    font-size: 16px;
    font-weight: 700;
    color: ${charcoal};
  }

  [data-mc-pdf-root] .mc-pdf-letter-intro {
    margin: 0;
    position: relative;
    z-index: 1;
    font-family: ${bodyFontFamily};
    font-size: 13px;
    line-height: 1.55;
    color: ${charcoal};
  }

  [data-mc-pdf-root] .mc-pdf-letter-contract-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  [data-mc-pdf-root] .mc-pdf-letter-contract-cell {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    border-radius: 12px;
    padding: 12px 14px;
    font-family: ${bodyFontFamily};
  }

  [data-mc-pdf-root] .mc-pdf-letter-contract-icon {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  [data-mc-pdf-root] .mc-pdf-letter-contract-label {
    margin: 0;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  [data-mc-pdf-root] .mc-pdf-letter-contract-value {
    margin: 5px 0 0;
    font-family: ${displayFontFamily};
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
    color: ${charcoal};
  }

  [data-mc-pdf-root] .mc-pdf-letter-next {
    display: flex;
    align-items: center;
    gap: 10px;
    background: ${cardWhite};
    border: 1px solid ${border};
    border-radius: 12px;
    padding: 10px 14px;
    font-family: ${bodyFontFamily};
  }

  [data-mc-pdf-root] .mc-pdf-letter-next-text {
    margin: 0;
    font-family: ${displayFontFamily};
    font-size: 12px;
    font-weight: 600;
    color: ${charcoal};
    line-height: 1.45;
  }

  [data-mc-pdf-root] .mc-pdf-letter-note {
    position: relative;
    background: ${cardWhite};
    border: 1px solid ${border};
    border-radius: 14px;
    padding: 14px 16px 12px;
    font-family: ${bodyFontFamily};
    overflow: hidden;
  }

  [data-mc-pdf-root] .mc-pdf-letter-note-quote {
    position: absolute;
    top: 2px;
    left: 12px;
    font-family: ${displayFontFamily};
    font-size: 48px;
    line-height: 1;
    color: ${mutedGray};
    opacity: 0.18;
    pointer-events: none;
  }

  [data-mc-pdf-root] .mc-pdf-letter-note-title {
    margin: 0;
    padding-left: 30px;
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${terracotta};
  }

  [data-mc-pdf-root] .mc-pdf-letter-note-body {
    margin: 10px 0 0;
    font-family: ${displayFontFamily};
    font-size: 13px;
    line-height: 1.55;
    color: ${charcoal};
  }

  [data-mc-pdf-root] .mc-pdf-letter-note-footer {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 10px;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid ${border};
  }

  [data-mc-pdf-root] .mc-pdf-letter-note-complete {
    margin: 0;
    font-size: 8px;
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${mutedGray};
  }

  [data-mc-pdf-root] .mc-pdf-letter-note-sign {
    margin: 0;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${terracotta};
  }

  [data-mc-pdf-root] .mc-pdf-letter-head {
  [data-mc-pdf-root] .mc-pdf-activations-weeks .mc-pdf-card {
    padding: 8px 10px !important;
  }

  /*
   * Activations fill — column grows to A4; Si Hua cards stay content-sized
   * (Canva density). Leftover space sits between weeks and the Month Trap.
   */
  [data-mc-pdf-root] .mc-pdf-activations-fill {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }

  [data-mc-pdf-root] .mc-pdf-activations-sihua-block {
    flex: 0 0 auto;
  }

  [data-mc-pdf-root] .mc-pdf-activations-sihua {
    flex: 0 0 auto;
    align-content: start;
    gap: 14px;
  }

  /* Extra air between Si Hua block and week plan (on top of column gap). */
  [data-mc-pdf-root] .mc-pdf-activations-weeks-block {
    margin-top: 28px;
  }

  [data-mc-pdf-root] .mc-pdf-grid-2x2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
    width: 100%;
    box-sizing: border-box;
  }

  [data-mc-pdf-root] .mc-pdf-protect-banner,
  [data-mc-pdf-root] .mc-pdf-trap-banner {
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /*
   * Cover poster stack — fills the A4 shell so chrome + absolute bg share
   * the same 210×297 mm box. Inline flex styles on the chapter remain.
   */
  [data-mc-pdf-root] .mc-pdf-cover-poster {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    box-sizing: border-box;
  }

  /*
   * Cover celestial background — user-supplied Canva full-bleed art
   * (watercolor + wheel + planets). Absolute fill under bars/title.
   * Asset is ~1:1.573 vs A4 ~1:1.414 — object-fit:cover crops top/bottom
   * (~11% total) while keeping full width. Global img max-width:100% would
   * squash bleed sizing; override here.
   * Do NOT set overflow:hidden on html/body/[data-mc-pdf-root].
   */
  [data-mc-pdf-root] .mc-pdf-cover-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    max-width: none !important;
    max-height: none !important;
    display: block;
    object-fit: cover;
    object-position: center center;
    pointer-events: none;
    z-index: 0;
  }

  [data-mc-pdf-root] .mc-pdf-section-rule {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }

  [data-mc-pdf-root] .mc-pdf-section-rule::after {
    content: "";
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, ${terracotta}55, transparent);
  }
`;
